import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const SUBSCRIPTION_LABEL = {
  tier_13: "MIRRORS · $13/mo activated",
  tier_33: "GODMODE · Lifetime activated",
};

// Strip `checkout=...` and `session_id=...` from the visible URL without
// triggering a navigation/re-render.
function cleanCheckoutQuery() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
  } catch {}
}

// Module-scope guard so React StrictMode's double-mount in dev (and any
// rapid component remount) only fires one polling loop per session_id.
const ACTIVATED_SESSIONS = new Set();

async function pollAndActivate(sessionId, maxTries = 6, delayMs = 1500) {
  for (let i = 0; i < maxTries; i++) {
    try {
      const { data } = await axios.post(
        `${API}/payments/v1/activate-subscription`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      if (data?.status === "activated" || data?.status === "purchased") return data;
    } catch (e) {
      // Transient — keep polling
    }
    // Bump the txn status via Stripe lookup so the next activate call sees "paid"
    try {
      await axios.get(`${API}/payments/v1/checkout/status/${sessionId}`);
    } catch {}
    await new Promise(r => setTimeout(r, delayMs));
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback (hash has session_id),
    // skip the /me check. AuthCallback handles the session exchange first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();

    // Auto-activate Stripe subscription on return from checkout
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const checkoutStatus = params.get("checkout");
    if (checkoutStatus === "cancelled") {
      toast.message("Checkout cancelled");
      cleanCheckoutQuery();
      return;
    }
    if (checkoutStatus !== "success" || !sessionId) return;
    // StrictMode + remounts dedupe — only the first mount per session_id polls.
    if (ACTIVATED_SESSIONS.has(sessionId)) return;
    ACTIVATED_SESSIONS.add(sessionId);

    (async () => {
      const result = await pollAndActivate(sessionId);
      cleanCheckoutQuery();
      if (!result) {
        toast.error("Activation pending — refresh in a moment if your access doesn't appear.");
        return;
      }
      if (result.status === "activated") {
        const pkg = result.subscription;
        toast.success(SUBSCRIPTION_LABEL[pkg] || "Subscription activated");
      } else if (result.status === "purchased") {
        toast.success("Purchase complete — album added to your collection");
      }
      await checkAuth();
    })();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const activateGodmode = async (code) => {
    try {
      const { data } = await axios.post(`${API}/auth/godmode-code`, { code }, { withCredentials: true });
      await checkAuth();
      return { ok: true, until: data.godmode_until, tier: data.tier };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const isPaid = () => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.subscription) return true;
    if (user.godmode_until) {
      return new Date(user.godmode_until) > new Date();
    }
    return false;
  };

  // Returns a short label describing the user's current access state, or "" if
  // not paid. Used by the header status chip.
  const subscriptionLabel = () => {
    if (!user) return "";
    if (user.role === "admin") return "ADMIN";
    if (user.subscription === "tier_33") return "GODMODE";
    if (user.subscription === "tier_13") return "MIRRORS";
    if (user.godmode_until) {
      const until = new Date(user.godmode_until);
      if (until > new Date()) {
        // > 1 year = lifetime sentinel
        const days = (until - new Date()) / (1000 * 60 * 60 * 24);
        return days > 365 ? "LIFETIME" : "24H";
      }
    }
    return "";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, activateGodmode, isPaid, subscriptionLabel, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
